import { defineStore } from 'pinia'
import axios from 'axios'
import { ref } from 'vue'
import { ensureAccessToken } from '@/utils/authSession'

export const useDashboardStore = defineStore('dashboard', () => {
  const stats = ref({
    alreadyAccountingListCount: 0,
    alreadyToCaseToDoListCount: 0,
    alreadyToReviewToDoListCount: 0,
    approvedPendingContractListCount: 0,
    alreadyToAppropriationToDoListCount: 0,
    alreadyToAppropriationListCount: 0,
    canceledListCount: 0,
    conditionalApprovalListCount: 0,
    pendingSupplementListCount: 0,
    readyToCaseToDoListCount: 0,
    rejectedListCount: 0,
    returnedListCount: 0,
    temporaryListCount: 0,
    webConfirmedListCount: 0,
    webTemporaryListCount: 0,
    webToContactListCount: 0,
    selfCancelListCount: 0,
    transferListCount: 0,
    supplementListCount: 0,
    supplementReturnListCount: 0,
    appropriationReturnListCount: 0,
    electronicContractReturnListCount: 0,
  })

  let fetchPromise: Promise<void> | null = null

  const loadStats = async () => {
    try {
      // 1. 取得登入的 Token
      const token = await ensureAccessToken()
      if (!token) return // 如果沒 Token 就不要發送請求

      // 🌟 強制確保 baseUrl 絕對有值。如果 .env 是空的或只寫了 "/"，強制 fallback 到後端 Port
      let baseUrl = import.meta.env.VITE_API_BASE_URL as string
      if (!baseUrl || baseUrl.trim() === '' || baseUrl === '/') {
        baseUrl = 'https://localhost:7261' // 請確認這是你 .NET 啟動的真實 Port
      }

      // 移除結尾的斜線避免雙斜線 (防呆)
      baseUrl = baseUrl.replace(/\/$/, '')

      // 3. 發送請求並強制帶上 Authorization 標頭
      const response = await axios.get(`${baseUrl}/api/Dashboard/stats`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })

      if (response.data.success) {
        const rawData = response.data.data
        // 4. 無論後端 C# 傳來的是大寫還是小寫，這裡通通吃下來
        stats.value = {
          alreadyAccountingListCount:
            rawData.alreadyAccountingListCount || rawData.AlreadyAccountingListCount || 0,
          alreadyToCaseToDoListCount:
            rawData.alreadyToCaseToDoListCount || rawData.AlreadyToCaseToDoListCount || 0,
          alreadyToReviewToDoListCount:
            rawData.alreadyToReviewToDoListCount || rawData.AlreadyToReviewToDoListCount || 0,
          approvedPendingContractListCount:
            rawData.approvedPendingContractListCount ||
            rawData.ApprovedPendingContractListCount ||
            0,
          alreadyToAppropriationToDoListCount:
            rawData.alreadyToAppropriationToDoListCount ||
            rawData.AlreadyToAppropriationToDoListCount ||
            0,
          alreadyToAppropriationListCount:
            rawData.alreadyToAppropriationListCount || rawData.AlreadyToAppropriationListCount || 0,
          canceledListCount:
            (rawData.canceledListCount ?? rawData.CanceledListCount ?? 0) +
            (rawData.systemCanceledListCount ?? rawData.SystemCanceledListCount ?? 0) +
            (rawData.invalidListCount ?? rawData.InvalidListCount ?? 0),
          conditionalApprovalListCount:
            rawData.conditionalApprovalListCount || rawData.ConditionalApprovalListCount || 0,
          pendingSupplementListCount:
            rawData.pendingSupplementListCount || rawData.PendingSupplementListCount || 0,
          readyToCaseToDoListCount:
            rawData.readyToCaseToDoListCount || rawData.ReadyToCaseToDoListCount || 0,
          rejectedListCount: rawData.rejectedListCount || rawData.RejectedListCount || 0,
          returnedListCount: rawData.returnedListCount || rawData.ReturnedListCount || 0,
          temporaryListCount: rawData.temporaryListCount || rawData.TemporaryListCount || 0,
          webConfirmedListCount:
            rawData.webConfirmedListCount || rawData.WebConfirmedListCount || 0,
          webTemporaryListCount:
            rawData.webTemporaryListCount || rawData.WebTemporaryListCount || 0,
          webToContactListCount:
            rawData.webToContactListCount || rawData.WebToContactListCount || 0,
          selfCancelListCount: rawData.selfCancelListCount || rawData.SelfCancelListCount || 0,
          transferListCount: rawData.transferListCount || rawData.TransferListCount || 0,
          supplementListCount: rawData.supplementListCount || rawData.SupplementListCount || 0,
          supplementReturnListCount:
            rawData.supplementReturnListCount || rawData.SupplementReturnListCount || 0,
          appropriationReturnListCount:
            rawData.appropriationReturnListCount || rawData.AppropriationReturnListCount || 0,
          electronicContractReturnListCount:
            rawData.electronicContractReturnListCount || rawData.ElectronicContractReturnListCount || 0,
        }
      }
    } catch (error) {}
  }

  const fetchStats = async () => {
    if (fetchPromise) return fetchPromise
    fetchPromise = loadStats()

    try {
      await fetchPromise
    } finally {
      fetchPromise = null
    }
  }

  return { stats, fetchStats }
})
